import Layout from '#components/ui/Layout';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import ProtectedRoute from '../auth/ProtectedRoute';

interface Route {
  path: string;
  component: typeof React.Component | any;
  children?: Array<Route>;
  permissions?: Array<string>;
}

interface RouteFormat {
  children: any[];
  element: JSX.Element;
  path: string;
}

const addPermissionParentToChildren = (routes: any, parentPermission: string | string[]) => {
  return routes.map((item) => {
    const { permissions, ...rest } = item;

    const mergePermission = new Set();
    [parentPermission, permissions].forEach((permission) => {
      if (permission) {
        if (Array.isArray(permission)) {
          permission.forEach((item) => {
            mergePermission.add(item);
          });
        } else {
          mergePermission.add(permission);
        }
      }
    });

    return {
      ...rest,
      permissions: Array.from(mergePermission),
    };
  });
};

const withRoutes = (routes: Array<Route>, root = true) => {
  const result: RouteFormat[] = [];

  routes.forEach((item) => {
    const { component: Comp, customHeader = false, children, permissions, ...rest } = item;
    const route = {
      ...rest,
      element:
        root && !customHeader ? (
          <Layout>
            <Comp />
          </Layout>
        ) : (
          <Comp />
        ), // public route
      ...(Array.isArray(children) &&
        children.length > 0 && {
          children: withRoutes(addPermissionParentToChildren(children, permissions), false),
        }),
    };

    if (permissions) {
      route.element = (
        <ProtectedRoute path={rest.path} {...{ permissions }}>
          {route.element}
        </ProtectedRoute>
      );
    }

    result.push(route);
  });

  return result;
};

export const RoutesMap = ({
  routes,
  children,
}: {
  routes: RouteFormat[];
  children: JSX.Element[];
}) => {
  if (!routes) return null;

  return (
    <Routes>
      {routes.map(({ path, element, children }) => {
        if (!Array.isArray(children)) return <Route key={path} {...{ path, element }} />;

        return (
          <Route key={path} element={element} path={path}>
            {children.map(({ path: childPath, element: childElement }) => (
              <Route key={childPath} element={childElement} path={childPath} />
            ))}
          </Route>
        );
      })}
      {children}
    </Routes>
  );
};

export default withRoutes;
